/** @jsxImportSource @emotion/react */
import { faArrowRight, faCheckCircle, faPaperPlane } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { Container } from "reactstrap";
import { RootState } from "../../../../../reducers";
import { triggerCheckBusinessRules } from "../../../../../actions/Checks/actions";
import useConfig from "../../../../../actions/Tenants/config/configHook";
import { AvosAction } from "../../../../../actions/types";
import { callAvosApi } from "../../../../../utils/useAvosApiHook";
import { Button } from "../../../../Helper/Button";
import LayerBreadcrumbs from "../../../Layers/Layer/LayerBreadcrumbs";
import ScanLayerButton from "../../../Layers/ScanLayerButton";
import { PageHeader } from "../../../Layout/PageHeader";
import { useQcEntities } from "../../QcEntityContext";
import useCheckFlowNavigate from "../../useCheckFlowNavigate";

export default function SendExternalNotificationStep() {
    const dispatch = useDispatch();
    const params = useParams();
    const tenantId = params.tenant_id as string;
    const { navigateToCheckAction, exitCheckFlow } = useCheckFlowNavigate();
    const queryClient = useQueryClient();

    const { check, layer } = useQcEntities();
    const tenantName = useSelector((state: RootState) => state.auth.user?.tenant?.tenant_name);

    const config = useConfig();
    const location = config.get_check_type_config(layer, check);

    const [isSending, setIsSending] = useState(false);
    // local flag to show success message after sending in this session
    const [sentThisSession, setSentThisSession] = useState(false);
    // check meta flag: was a notification sent in a previous session?
    const wasPreviouslySent = check?.check_status_sent_to_tenant === "true";

    const [searchParams] = useSearchParams();
    const isNextPallet = searchParams.get("nextPallet") === "true";

    const proceed = () => {
        if (location?.cta_form_title) {
            navigateToCheckAction("send-status-to-tenant/modal/cta");
            return;
        }
        exitCheckFlow();
    };

    const handleSend = async () => {
        setIsSending(true);
        try {
            // Synchronous send via callAvosApi so HTTP errors (including the
            // external system's rejection) reject the promise and are toasted
            // by the axios interceptor; we only mark success when it resolves.
            await callAvosApi(`/checks/${check.check_id}/send-status-to-tenant`, { method: "POST" });
            setSentThisSession(true);
            toast.success(`Check status sent to ${tenantName}.`);
            // Refresh check so check_status_sent_to_tenant meta is reflected on next visit
            queryClient.invalidateQueries({ queryKey: ["check", tenantId, check.check_id] });
        } catch {
            // Error toast already shown by the axios interceptor.
        } finally {
            setIsSending(false);
        }
    };

    const sendButtonLabel = isSending ? "Sending..." : "Send check status";

    const statusMessage = () => {
        if (sentThisSession) {
            return (
                <p className="text-success mb-4">
                    <FontAwesomeIcon icon={faCheckCircle} className="me-2" />
                    Check status has been sent to {tenantName}.
                </p>
            );
        }
        if (wasPreviouslySent) {
            return (
                <p className="text-muted mb-4">
                    <FontAwesomeIcon icon={faCheckCircle} className="me-2 text-success" />
                    Previously sent to {tenantName}. You can send again.
                </p>
            );
        }
        return (
            <p className="text-muted mb-4">
                Do you want to send the check status to {tenantName}?
            </p>
        );
    };

    return (
        <Container>
            <PageHeader>
                <LayerBreadcrumbs />
            </PageHeader>

            <div className="bg-body rounded shadow p-2 p-lg-4 my-2 my-md-4">
                <div className="text-center py-4">
                    <h4 className="mb-3">Check Completed</h4>

                    {statusMessage()}

                    <div className="d-flex flex-wrap gap-2 justify-content-center">
                        <Button
                            color="primary"
                            disabled={isSending}
                            onClick={handleSend}
                            icon={faPaperPlane}
                        >
                            {sendButtonLabel}
                        </Button>

                        <Button
                            color="secondary"
                            outline
                            disabled={isSending}
                            onClick={proceed}
                            icon={faArrowRight}
                        >
                            Continue
                        </Button>

                        {location?.show_scanner_button_on_check_page && isNextPallet && (
                            <ScanLayerButton
                                modal
                                label="Next pallet"
                                initialCheckType={check?.location}
                                onClick={() => {
                                    dispatch(triggerCheckBusinessRules(tenantId, check.check_id) as AvosAction);
                                }}
                            />
                        )}
                    </div>
                </div>
            </div>
        </Container>
    );
}
